/**
 * E2EE Crypto Store — Zustand
 *
 * Manages end-to-end encryption state:
 * - Device key generation and persistence (IndexedDB)
 * - Session management per peer (X3DH + Double Ratchet)
 * - Encrypt/decrypt functions for chat integration
 * - Device key registration with server
 * - Group E2EE via Sender Keys (see useCrypto-group.ts)
 * - Message backups via Backup Key (Ansatz C, see backup-key.ts)
 *
 * Helper utilities: useCrypto-helpers.ts
 * Self-encryption:  useCrypto-self.ts
 * Session mgmt:     useCrypto-sessions.ts
 * Encrypt/decrypt:  useCrypto-encrypt.ts
 * PreKey mgmt:      useCrypto-prekeys.ts
 */

import { create } from "zustand";
import { api, ApiError } from "../lib/api";
import {
  generateDeviceKeys,
  generateFallbackPreKey,
  exportPublicKeys,
  type DeviceKeys,
  type CryptoSession,
} from "@kollegeplus/crypto";
import {
  saveDeviceKeys,
  getFirstDeviceKeys,
  eraseAllCryptoData,
  deleteSessionsForPeer,
  deleteDeviceKeysByDeviceId,
  cachePlaintext,
} from "../lib/crypto-storage";
import type { ServerDeviceKeys, MultiDeviceRecipient } from "@kollegeplus/shared";

// Re-export for consumers that import from useCrypto
export type { E2EEEnvelope, MultiDeviceRecipient } from "@kollegeplus/shared";
import { e2eeLog } from "../lib/e2ee-logger";
import { withBusy } from "../lib/busy-guard";
import { ws } from "../lib/ws";
import { reportE2eeError } from "../lib/e2ee-telemetry";
import { runSelfCanary } from "../lib/crypto-canary";
import { computeIdkFingerprint } from "../lib/idk-fingerprint";
import {
  detectWipe,
  maybeRekey,
  readLastKnownDeviceId,
} from "../lib/rekey";
import { nextSpkId } from "../lib/spk-id";
import { applySpkMigrationOnInit } from "../lib/spk-migrate";
import { onDeviceKeysChanged } from "../lib/crypto-tab-sync";

// ── Extracted modules ──
import {
  generateDeviceId,
  getBrowserName,
  serializeDeviceKeys,
  deserializeDeviceKeys,
} from "./useCrypto-helpers";
import {
  encryptForGroup as _encryptForGroup,
  decryptGroupMessage as _decryptGroupMessage,
  rotateSenderKey as _rotateSenderKey,
  decryptBotChannelSenderKeyMessage as _decryptBotChannelSenderKeyMessage,
  isValidBotChannelSenderKeyEnvelope,
  type BotChannelSenderKeyEnvelope,
  p4DrainEpochAcks,
  drainDeliveryAcks,
} from "./useCrypto-group";
export type { BotChannelSenderKeyEnvelope };
export { isValidBotChannelSenderKeyEnvelope };
import {
  initBackupKey as _initBackupKey,
  uploadBackup as _uploadBackup,
  restoreBackupKeyFromCache,
  clearBackupKeyCache,
  restoreBackups,
} from "../lib/backup-key";
import {
  getOrCreateSession as _getOrCreateSession,
  getOrCreateSessionForDevice as _getOrCreateSessionForDevice,
} from "./useCrypto-sessions";
import {
  encryptForAllDevices as _encryptForAllDevices,
  encryptForPeer as _encryptForPeer,
  decryptMessage as _decryptMessage,
  guardCryptoHealthy,
} from "./useCrypto-encrypt";
export { RecipientNotReadyError, EmptyGroupError, CryptoBrokenError } from "./useCrypto-encrypt";
export type { EncryptFanoutMeta } from "./useCrypto-encrypt";
import type { EncryptFanoutMeta } from "./useCrypto-encrypt";
import { checkAndReplenishPreKeys as _checkAndReplenishPreKeys } from "./useCrypto-prekeys";

// Re-export getPersistedDeviceId so existing consumers keep working
export { getPersistedDeviceId } from "./useCrypto-helpers";

// Re-export selfDecrypt so existing consumers keep working
export { selfDecrypt } from "./useCrypto-self";

// ── Bug D: user_backup_key auto-pull ────────────────────────────────────────
// In-memory flag: only send one backup_key_request per session.
// Throttled further on the server side (1× / 5 min per userId).
let _backupKeyRequestSent = false;

/**
 * Fire-and-forget: ask peer devices of the same user to send us the
 * user_backup_key via X3DH so we can decrypt peer_sender_key_backups.
 * No-op if already sent this session or crypto not yet initialized.
 */
export function requestUserBackupKey(): void {
  if (_backupKeyRequestSent) return;
  const state = useCrypto.getState();
  if (!state.initialized || !state.deviceId) return;
  _backupKeyRequestSent = true;
  ws.send({ type: "backup_key_request", requesterDeviceId: state.deviceId });
  e2eeLog.info("[Bug D] backup_key_request sent, requesterDeviceId=", state.deviceId);
  void import("../lib/e2ee-telemetry").then(({ reportE2eeError }) => {
    reportE2eeError({ phase: "backup_key_request_sent", deviceId: state.deviceId });
  }).catch(() => {});
}

// ============================================================
// Store
// ============================================================

/**
 * Edge Case Notes:
 *
 * 1. Offline Recipients: Messages are stored server-side as ciphertext. When the
 *    recipient loads messages, loadMessages() in useChat decrypts them. The Double
 *    Ratchet protocol handles out-of-order messages via skipped message keys.
 *
 * 2. Multi-Device: Each device registers independently with unique keys. Currently,
 *    messages are encrypted for only the first available device of a peer. Full
 *    multi-device support (encrypting for all devices) is a future enhancement.
 *    Each device maintains separate sessions and sender keys.
 *
 * 3. Decryption Failure: Returns null from decrypt functions, which useChat maps
 *    to "[Nachricht konnte nicht entschlüsselt werden]" for display. This handles:
 *    - Missing sessions (peer re-installed / cleared data)
 *    - Corrupted ciphertext
 *    - Key mismatch (wrong device / stale session)
 *    - Missing sender keys for group messages
 */

export interface CryptoState {
  /** Whether E2EE is initialized */
  initialized: boolean;
  /** Our device ID */
  deviceId: string | null;
  /** Our device keys (in memory) */
  deviceKeys: DeviceKeys | null;
  /** Active sessions: peerId:peerDeviceId -> CryptoSession */
  sessions: Map<string, CryptoSession>;
  /** Loading state */
  isInitializing: boolean;

  /** F19c: result of the boot-time self-encrypt/decrypt canary.
   *  'unknown' = not yet run; 'ok' = roundtrip succeeded;
   *  'broken' = local crypto state is corrupt — UI should banner the user
   *  and prompt them to logout + clear site data. */
  // TODO(F19c-ui): consume cryptoHealth in TopBar/Layout
  cryptoHealth: 'unknown' | 'ok' | 'broken';

  // ── Actions ──

  /** Initialize E2EE: load or generate device keys, register with server */
  initialize: () => Promise<void>;

  /** Encrypt a message for a DM peer */
  encryptForPeer: (peerId: string, plaintext: string) => Promise<string>;

  /** Decrypt a received message */
  decryptMessage: (senderId: string, envelopeStr: string) => Promise<string | null>;

  /** Encrypt a message for a group channel */
  encryptForGroup: (channelId: string, memberIds: string[], plaintext: string) => Promise<string>;

  /** Decrypt a group message. messageId enables IDB plaintext cache (Option A). */
  decryptGroupMessage: (senderId: string, channelId: string, envelopeStr: string, messageId?: string) => Promise<string | null>;

  /** Decrypt a bot-channel sender-key message (F16d). messageId enables IDB plaintext cache. */
  decryptBotChannelSenderKeyMessage: (channelId: string, botUserId: string, env: BotChannelSenderKeyEnvelope, messageId?: string) => Promise<string | null>;

  /** Erase all crypto data (logout) */
  eraseAll: () => Promise<void>;

  /** Delete all sessions for a specific peer (session reset) */
  deleteSessionForPeer: (peerId: string) => Promise<void>;

  /** Get or create a session for a peer (first available device — legacy) */
  getOrCreateSession: (peerId: string) => Promise<{ session: CryptoSession; peerDeviceId: string }>;

  /** Get or create a session for a specific peer device */
  getOrCreateSessionForDevice: (peerId: string, peerDeviceId: string, peerDevice: ServerDeviceKeys) => Promise<CryptoSession>;

  /** Encrypt a message for ALL devices of multiple recipients (multi-device).
   *  Pass `meta` to receive peer-device-existence + skipped-slot info (Fix 1). */
  encryptForAllDevices: (recipientIds: string[], plaintext: string, meta?: EncryptFanoutMeta) => Promise<MultiDeviceRecipient[]>;

  /** Rotate sender key for a group channel (call on member changes) */
  rotateSenderKey: (channelId: string, memberIds: string[]) => Promise<void>;

  /** Check and replenish pre-keys if below threshold */
  checkAndReplenishPreKeys: () => Promise<void>;

  // ── Ansatz C: Password-based Backup Key ──

  /** Backup key for message backup encryption (in-memory only) */
  backupKey: CryptoKey | null;

  /** Initialize backup key from password (call after login) */
  initBackupKey: (password: string) => Promise<void>;

  /** Upload a message backup (fire-and-forget for existing callers; F24:
   *  returns a promise resolving to success/failure so a caller that cares —
   *  e.g. cacheAndBackup's retry-on-failure — can observe the outcome). */
  uploadMessageBackup: (messageId: string, plaintext: string) => Promise<boolean>;
}

/**
 * In-memory cache for restored backup plaintexts.
 * Workaround for Safari/iOS IndexedDB timing issues — IDB writes may not be
 * immediately readable. This map is populated during backup restore and
 * consulted by decryptSingleMessage as a fast fallback before IDB.
 */
export const _restoredPlaintexts = new Map<string, string>();

// ── F15b: Phantom-Revival Registration Helper ──
//
// POST /users/me/devices returns 409 `device_deactivated_use_fresh_id` when
// the server has a soft-deactivated row for our deviceId older than 7 days.
// That row is almost certainly a phantom (stale browser tab whose deviceId
// outlived its server registration). Reviving it lets us silently subscribe
// to channels with no peer holding a working session — every send to us
// stays undecryptable.
//
// Recovery: mint a fresh deviceId (UUID v4), rewire localStorage + cookie +
// IDB to point to it, and retry the POST ONCE with the same keys. The keys
// themselves are reusable across deviceIds; only the registration anchor
// has to be fresh.
export async function _registerDeviceWithPhantomRetry(
  deviceId: string,
  publicKeys: ReturnType<typeof exportPublicKeys>,
  storedDeviceKeys: import("../lib/crypto-storage").StoredDeviceKeys | null,
  get: () => CryptoState,
  set: (partial: Partial<CryptoState>) => void,
): Promise<string> {
  // F22c Task 3: IndexedDB is the single source of truth for what this device
  // may publish. Tab memory can be hours stale (another tab replenished OPKs,
  // rotated the deviceId via phantom-retry, or healed drift via drift-reregister).
  // Re-read IDB at call time; substitute IDB material; refuse entirely when the
  // device identity has moved to a different deviceId (another tab's phantom-retry).
  const idbNow = await getFirstDeviceKeys();
  if (idbNow !== null && idbNow !== undefined) {
    if (idbNow.deviceId !== deviceId) {
      // Another tab performed a 409 phantom-retry and rotated the device identity.
      // Publishing the caller's stale deviceId would resurrect a zombie. Throw so
      // the caller can reload state from IDB instead of issuing a stale POST.
      throw new Error(
        `idb_device_mismatch: idb=${idbNow.deviceId} caller=${deviceId} — another tab rotated the device; reload state instead of publishing`,
      );
    }
    // Same deviceId: substitute IDB material so we never publish stale tab-memory
    // keys (the root cause of the June-7 SPK-pub churn: two tabs each captured a
    // different zustand init state and raced to POST /users/me/devices).
    storedDeviceKeys = idbNow;
    const idbKeys = deserializeDeviceKeys(idbNow);
    publicKeys = exportPublicKeys(idbKeys);
  }
  // idbNow === null: first-ever registration — passed-in material IS the truth,
  // proceed unchanged.

  // Keep kp_idk_fingerprint in lockstep with the bundle we actually publish.
  // ROOT CAUSE of the idle "Verschlüsselung out of sync" + reinit churn: the
  // re-register paths (heartbeat reregister, init needsRegistration, priorWipe
  // rekey, fresh-id retry) used to rewrite only kp_keys_fingerprint, leaving
  // kp_idk_fingerprint stale whenever the published SPK differed from the one it
  // was last computed for. The server then failed every idle /auth/refresh with
  // idk_fingerprint_mismatch → eraseAllCryptoData + full reinit, with zero user
  // activity. Every (re)registration funnels through this function, so writing
  // the fingerprint here from the published material can never lag again.
  const persistIdkFingerprint = async (): Promise<void> => {
    // fp is computed from key MATERIAL only (identity/signing/SPK pub), never the
    // deviceId — so it is correct on the fresh-id retry path too (same material,
    // new id). Skip only when there is no stored material (first-ever path: init
    // already wrote the fingerprint from the freshly generated keys).
    if (!storedDeviceKeys) return;
    try {
      const dk = deserializeDeviceKeys(storedDeviceKeys);
      const idkFp = await computeIdkFingerprint(
        dk.identityKeyPair.curve25519.publicKey,
        dk.identityKeyPair.ed25519.publicKey,
        dk.signedPreKey.keyPair.publicKey,
      );
      localStorage.setItem("kp_idk_fingerprint", idkFp);
    } catch (fpErr) {
      e2eeLog.warn("idk fingerprint recompute after register failed (non-fatal)", fpErr);
    }
  };

  const displayName = getBrowserName();

  // F22c Task 6: include expectedBundleVersion when the client has a stored
  // value from a previous register or heartbeat response. Absent on first
  // registration (localStorage key missing) — server keeps last-write-wins.
  // The version is stored under kp_bundle_version in localStorage; cleared
  // on phantom-retry below (fresh deviceId = fresh version state).
  const storedBundleVersion = localStorage.getItem("kp_bundle_version");
  const expectedBundleVersion = storedBundleVersion ? parseInt(storedBundleVersion, 10) : undefined;

  try {
    const resp = await api.post<{ success?: boolean; reactivationBlocked?: string; bundleVersion?: number }>(
      "/users/me/devices",
      {
        deviceId,
        displayName,
        identityKey: publicKeys.identityKey,
        signingKey: publicKeys.signingKey,
        signedPreKey: publicKeys.signedPreKey,
        preKeys: publicKeys.preKeys,
        fallbackPreKey: publicKeys.fallbackPreKey,
        // F22c Task 6: omit field when absent so legacy servers don't see an
        // unexpected field (they would ignore it, but cleaner to omit).
        ...(expectedBundleVersion !== undefined ? { expectedBundleVersion } : {}),
      },
    );
    // Server returns 200 { success: false, reactivationBlocked: "cascade_cooldown" }
    // when the device is in cascade cooldown — not an HTTP error, so we must check
    // the body. Throw a recognisable error so callers (device-heal, rekey) can gate
    // on it without having to duplicate the body-check.
    if (resp?.reactivationBlocked) {
      throw new Error(`reactivation_blocked:${resp.reactivationBlocked}`);
    }
    // F22c Task 6: persist the returned bundleVersion so the next register call
    // can supply the correct expectedBundleVersion.  NaN-safe: only write valid numbers.
    if (typeof resp?.bundleVersion === "number" && !isNaN(resp.bundleVersion)) {
      localStorage.setItem("kp_bundle_version", String(resp.bundleVersion));
    }
    await persistIdkFingerprint();
    return deviceId;
  } catch (err) {
    if (err instanceof Error && err.message.startsWith("reactivation_blocked")) {
      throw err; // Already the canonical shape — propagate unchanged.
    }
    // F22c Task 6: bundle_conflict — another tab (or the server) wrote a newer
    // bundle between this client's last read and now.  Adopt the server's current
    // version so the next heartbeat / re-trigger picks it up correctly.  Do NOT
    // retry immediately: the cross-tab notify (Task 4) or the next heartbeat
    // will re-trigger the correct publish; an immediate retry would just conflict
    // again if two tabs are racing. The 409 error is distinguished from the
    // phantom-retry 409 (device_deactivated_use_fresh_id) solely by the `error`
    // field in the response body, which ApiError maps to `err.code`.
    if (
      err instanceof ApiError &&
      err.status === 409 &&
      err.code === "bundle_conflict"
    ) {
      const currentBundleVersion = (err.data as { currentBundleVersion?: number } | null)?.currentBundleVersion;
      if (typeof currentBundleVersion === "number" && !isNaN(currentBundleVersion)) {
        localStorage.setItem("kp_bundle_version", String(currentBundleVersion));
        e2eeLog.warn(
          "F22c: bundle_conflict — adopted server currentBundleVersion, no immediate retry",
          { expectedBundleVersion, currentBundleVersion },
        );
      }
      // Return the deviceId unchanged — the caller will not re-throw and the
      // next heal/heartbeat path picks up the now-correct version from localStorage.
      // Same device, same IDB material → its fingerprint matches the bundle the
      // winning writer published, so persisting it here stays correct.
      await persistIdkFingerprint();
      return deviceId;
    }
    if (
      err instanceof ApiError &&
      err.status === 409 &&
      err.code === "device_deactivated_use_fresh_id"
    ) {
      // Phantom revival blocked — regenerate deviceId and retry ONCE.
      const oldDeviceId = deviceId;
      const freshId = `web-${typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID().replace(/-/g, "").slice(0, 16)
        : Math.random().toString(36).slice(2, 18)}`;
      e2eeLog.warn(
        "F15b: Server rejected stale deviceId — regenerating",
        { oldDeviceId, newDeviceId: freshId },
      );

      // Rewrite localStorage + cookie via the helper
      localStorage.setItem("kollegeplus:deviceId", freshId);
      // F22c Task 6: clear bundle version — fresh deviceId starts at version 1.
      localStorage.removeItem("kp_bundle_version");
      try {
        const maxAge = 400 * 24 * 60 * 60;
        const secure = location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `kp_deviceId=${encodeURIComponent(freshId)}; path=/; max-age=${maxAge}; SameSite=Strict${secure}`;
      } catch { /* non-critical */ }

      // Drop the old IDB device-keys row and store under the new deviceId
      await deleteDeviceKeysByDeviceId(oldDeviceId);
      if (storedDeviceKeys) {
        const refreshed: import("../lib/crypto-storage").StoredDeviceKeys = {
          ...storedDeviceKeys,
          deviceId: freshId,
        };
        await saveDeviceKeys(refreshed);
      }

      // Propagate to store before retry — downstream paths read get().deviceId
      set({ deviceId: freshId });

      // Retry POST once with the fresh deviceId. The F22c IDB-truth guard
      // (getFirstDeviceKeys above) already ran at function entry before this
      // catch block; freshId + its keys are now in IDB (saveDeviceKeys above).
      // The retry goes directly to api.post — no recursive guard trip.
      // No expectedBundleVersion on retry: fresh deviceId has no stored version.
      const retryResp = await api.post<{ bundleVersion?: number }>("/users/me/devices", {
        deviceId: freshId,
        displayName,
        identityKey: publicKeys.identityKey,
        signingKey: publicKeys.signingKey,
        signedPreKey: publicKeys.signedPreKey,
        preKeys: publicKeys.preKeys,
      });
      // F22c Task 6: persist version from fresh-id registration response.
      if (typeof retryResp?.bundleVersion === "number" && !isNaN(retryResp.bundleVersion)) {
        localStorage.setItem("kp_bundle_version", String(retryResp.bundleVersion));
      }
      e2eeLog.info("F15b: Re-registered with fresh deviceId after 409", { newDeviceId: freshId });
      // Bust the "current state" cache via get() so subsequent code paths see the new id.
      void get;
      // Key material is unchanged across the fresh-id retry (only deviceId moved),
      // so the fingerprint computed from storedDeviceKeys still matches the bundle.
      await persistIdkFingerprint();
      return freshId;
    }
    throw err;
  }
}

// ── Idle-heartbeat scheduler ──
// Long-open tabs must keep `devices.last_active_at` fresh so the per-user
// web-device cap (`user.routes.ts`) never reaps a still-live tab. The init
// path already fires one heartbeat; this module-level scheduler keeps it
// going every 6h and on visibility→visible transitions (debounced to 5min).
const HEARTBEAT_INTERVAL_MS = 1 * 60 * 60 * 1000;
const REPLENISH_INTERVAL_MS = 15 * 60 * 1000;
const HEARTBEAT_VISIBILITY_THROTTLE_MS = 5 * 60 * 1000;
let heartbeatLastFiredAt = 0;
let heartbeatInFlight = false;
let heartbeatHandlersRegistered = false;

// ── F22c Task 4: cross-tab device-key change subscription ──
// Module-level guard (same pattern as heartbeatHandlersRegistered) ensures we
// subscribe at most once even if initialize() is called multiple times.
let tabSyncSubscribed = false;

async function _runIdleHeartbeat(
  get: () => CryptoState,
  set: (partial: Partial<CryptoState>) => void,
): Promise<void> {
  if (heartbeatInFlight) return;
  const state = get();
  if (!state.initialized || !state.deviceId || !state.deviceKeys) return;
  heartbeatInFlight = true;
  heartbeatLastFiredAt = Date.now();
  const deviceId = state.deviceId;
  const publicKeys = exportPublicKeys(state.deviceKeys);
  const fingerprint = publicKeys.identityKey + ":" + publicKeys.signingKey;
  try {
    // P4: drain any pending epoch acks accumulated since the last heartbeat.
    // Batched here (≤20 per call) so there is zero extra per-message round-trip.
    // Undrainable remainder stays in the buffer and goes out on the next heartbeat.
    const epochAcks = p4DrainEpochAcks();
    // Task 2: drain sender-key delivery acks the same way (parallel array).
    const deliveryAcks = drainDeliveryAcks();

    // F22b D3: include local SPK keyId and IDK fingerprint so the server can
    // detect drift between what the client has in IDB and what was last uploaded.
    const hbRes = await api.post<{ success: boolean; reregister?: boolean; serverPreKeyCount?: number; spkDrift?: boolean; bundleVersion?: number }>(
      "/users/me/devices/heartbeat",
      {
        deviceId,
        spkKeyId: state.deviceKeys.signedPreKey.keyId,
        idkFingerprintLocal: localStorage.getItem("kp_idk_fingerprint") ?? undefined,
        // P4: include epoch acks (may be empty — server ignores undefined/empty)
        ...(epochAcks.length > 0 ? { epochAcks } : {}),
        // Task 2: include sender-key delivery acks (omit when empty)
        ...(deliveryAcks.length > 0 ? { deliveryAcks } : {}),
      },
    );
    // F22c Task 6: persist bundleVersion from heartbeat so the client has a
    // fresh value even when no re-register happened. Cheap resync path.
    if (typeof hbRes?.bundleVersion === "number" && !isNaN(hbRes.bundleVersion)) {
      localStorage.setItem("kp_bundle_version", String(hbRes.bundleVersion));
    }
    if (hbRes?.reregister && hbRes?.spkDrift) {
      // F22c/FC1: drift self-heal — re-publish IndexedDB truth, NOT stale tab
      // memory. The stale-memory re-publish (Tab-memory race via useCrypto.getState()
      // or exportPublicKeys(state.deviceKeys) captured at init) was the root cause
      // of the 2026-06-07 SPK-pub churn; see Task 0 findings in the F22c plan.
      // Rate-limited to once per 10min so a flapping comparison cannot storm.
      e2eeLog.warn("[F22c] Idle heartbeat: server reported SPK drift — triggering IDB-truth re-register");
      void triggerDriftSelfHeal();
    } else if (hbRes?.reregister) {
      // Non-drift re-register: server signalled device_not_found or low prekeys.
      // The passed-in publicKeys may be init-time stale, but that is harmless:
      // _registerDeviceWithPhantomRetry (F22c Task 3) re-reads IndexedDB at
      // entry and substitutes IDB truth before publishing.
      e2eeLog.warn("Idle heartbeat: server requested re-registration");
      const stored = (await getFirstDeviceKeys()) ?? null;
      await _registerDeviceWithPhantomRetry(deviceId, publicKeys, stored, get, set);
      localStorage.setItem("kp_keys_fingerprint", fingerprint);
      e2eeLog.info("Idle heartbeat: device re-registered");
    }
    if (typeof hbRes?.serverPreKeyCount === "number") {
      _checkAndReplenishPreKeys(get, set, hbRes.serverPreKeyCount).catch(() => {});
    }
  } catch (hbErr) {
    e2eeLog.warn("Idle heartbeat failed — re-registering keys", hbErr);
    try {
      const stored = (await getFirstDeviceKeys()) ?? null;
      await _registerDeviceWithPhantomRetry(deviceId, publicKeys, stored, get, set);
      localStorage.setItem("kp_keys_fingerprint", fingerprint);
    } catch (regErr) {
      e2eeLog.warn("Idle heartbeat re-registration also failed:", regErr);
    }
  } finally {
    heartbeatInFlight = false;
  }
}

function _ensureHeartbeatScheduler(
  get: () => CryptoState,
  set: (partial: Partial<CryptoState>) => void,
): void {
  if (heartbeatHandlersRegistered) return;
  if (typeof document === "undefined" || typeof window === "undefined") return;
  heartbeatHandlersRegistered = true;

  // Periodic heartbeat every 1h — detects server/local pre-key drift
  setInterval(() => {
    void _runIdleHeartbeat(get, set);
  }, HEARTBEAT_INTERVAL_MS);

  // Periodic pre-key replenish every 15min — closes background-tab gap where
  // no send/receive fires and WS stays alive (L1)
  setInterval(() => {
    void _checkAndReplenishPreKeys(get, set);
  }, REPLENISH_INTERVAL_MS);

  // Visibility trigger: replenish pre-keys when tab becomes visible
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - heartbeatLastFiredAt < HEARTBEAT_VISIBILITY_THROTTLE_MS) return;
    void _runIdleHeartbeat(get, set);
    // Also check pre-keys independently of heartbeat throttle
    _checkAndReplenishPreKeys(get, set).catch(() => {});
  });

  // WS-reconnect trigger: replenish pre-keys after every successful reconnect.
  // After a disconnect the pool may have drained (peers fetched OPKs offline);
  // the reconnect is the first reliable moment we can upload replacements.
  // Singleton — cleanup unnecessary; heartbeatHandlersRegistered ensures single registration.
  ws.on("_connected", () => {
    _checkAndReplenishPreKeys(get, set).catch(() => {});
  });

  // Server-push trigger: server notifies us when our OPK pool drops below 10
  // after a pre-key was consumed by another user's X3DH handshake.
  // Singleton — cleanup unnecessary; heartbeatHandlersRegistered ensures single registration.
  ws.on("prekey-pool-low", () => {
    e2eeLog.log("[prekey] Received prekey-pool-low from server — replenishing");
    _checkAndReplenishPreKeys(get, set).catch(() => {});
  });
}

export const useCrypto = create<CryptoState>((set, get) => ({
  initialized: false,
  deviceId: null,
  deviceKeys: null,
  sessions: new Map(),
  isInitializing: false,
  cryptoHealth: 'unknown',
  backupKey: null,

  initialize: async () => {
    if (get().initialized || get().isInitializing) return;
    set({ isInitializing: true });

    try {
      // Try to load existing device keys from IndexedDB
      let stored = await getFirstDeviceKeys();
      let deviceKeys: DeviceKeys;
      let deviceId: string;

      // Task 6: distinguish a key-store wipe (IDB + localStorage mirror both
      // gone, but we remember a prior deviceId) from a brand-new device. The
      // wipe policy (cascade-block check, telemetry, marker handling, no-retry
      // semantics) lives in lib/rekey.ts `maybeRekey`; here we only capture the
      // prior deviceId so the registration step below can route through it.
      const lastKnownDeviceId = stored ? null : readLastKnownDeviceId();
      const priorWipeDeviceId = detectWipe({ hasPrivateKeys: !!stored, lastKnownDeviceId })
        ? lastKnownDeviceId
        : null;

      if (stored) {
        // Restore from IndexedDB
        deviceId = stored.deviceId;
        // Sync localStorage to match IDB (ws.ts reads from localStorage)
        localStorage.setItem("kollegeplus:deviceId", deviceId);
        deviceKeys = deserializeDeviceKeys(stored);

        // Fix #1 migration: rotate a legacy keyId=1 SPK onto a device-unique id,
        // retaining the old SPK in history. Clears kp_keys_fingerprint when it
        // rotates so the registration block below republishes the new SPK.
        {
          const beforeSpkId = deviceKeys.signedPreKey.keyId;
          deviceKeys = applySpkMigrationOnInit(deviceKeys);
          if (deviceKeys.signedPreKey.keyId !== beforeSpkId) {
            const migratedStored = serializeDeviceKeys(deviceId, deviceKeys);
            await saveDeviceKeys(migratedStored);
            e2eeLog.info("[Fix#1] Migrated legacy SPK keyId → unique", {
              from: beforeSpkId,
              to: deviceKeys.signedPreKey.keyId,
            });
          }
        }
      } else {
        // Generate new device keys
        deviceId = generateDeviceId();
        deviceKeys = generateDeviceKeys(20, nextSpkId());

        // Persist to IndexedDB
        stored = serializeDeviceKeys(deviceId, deviceKeys);
        await saveDeviceKeys(stored);
      }

      // Fix 3 / eager provisioning: backfill a last-resort fallback pre-key for
      // devices created before Fix 3 (no fallbackPreKey in IDB). Without it the
      // server can only offer the weaker SPK-only X3DH once our OPK pool empties.
      // Persist it and force re-registration so the public half reaches the server.
      if (!deviceKeys.fallbackPreKey) {
        deviceKeys = { ...deviceKeys, fallbackPreKey: generateFallbackPreKey() };
        stored = serializeDeviceKeys(deviceId, deviceKeys);
        await saveDeviceKeys(stored);
        localStorage.removeItem("kp_keys_fingerprint");
        e2eeLog.info("Eager: generated missing fallback pre-key for existing device");
      }

      // F19c: detect local crypto-state corruption before peers start hitting us with X3DH.
      const canaryResult = await runSelfCanary(deviceKeys.identityKeyPair.curve25519.privateKey);
      if (canaryResult.ok) {
        set({ cryptoHealth: 'ok' });
      } else {
        reportE2eeError({ phase: "self_canary_failed", deviceId, error: canaryResult.error });
        e2eeLog.error("[F19c] self canary failed", canaryResult.error);
        set({ cryptoHealth: 'broken' });
        // Still proceed with init — UI banner will surface the broken state.
      }

      // Register device keys with server — only if keys changed
      const publicKeys = exportPublicKeys(deviceKeys);
      const keysFingerprint = publicKeys.identityKey + ":" + publicKeys.signingKey;
      const lastRegistered = localStorage.getItem("kp_keys_fingerprint");
      const needsRegistration = !lastRegistered || lastRegistered !== keysFingerprint;
      let _serverPreKeyCount: number | undefined;

      // F19b: recompute identity fingerprint EVERY init (never cached across
      // boots — SPK or identity may have rotated since last run). Sent on
      // every /auth/refresh so the server can fast-fail if our local key
      // material has silently diverged from the bundle it publishes for X3DH.
      try {
        const idkFp = await computeIdkFingerprint(
          deviceKeys.identityKeyPair.curve25519.publicKey,
          deviceKeys.identityKeyPair.ed25519.publicKey,
          deviceKeys.signedPreKey.keyPair.publicKey,
        );
        localStorage.setItem("kp_idk_fingerprint", idkFp);
      } catch (fpErr) {
        // Non-fatal: refresh will just skip the new validation path and
        // continue with the older code path that always allowed the refresh.
        e2eeLog.warn("F19b: idk fingerprint compute failed (non-fatal)", fpErr);
      }

      if (priorWipeDeviceId) {
        // Task 6 — key-store wipe path. The wiped device generated fresh keys in
        // the `!stored` branch above; SHIP the wipe re-key policy through the
        // tested `maybeRekey` (lib/rekey.ts) rather than reimplementing it here.
        // It owns: cascade-block check (skip + blocked UX), telemetry emit, the
        // durable-marker refresh, and the no-retry-loop on reactivation_blocked.
        // `registerNewDevice` just wraps the existing fresh-id registration path.
        const newDeviceId = await maybeRekey(
          { hasPrivateKeys: false, lastKnownDeviceId: priorWipeDeviceId },
          {
            registerNewDevice: () =>
              _registerDeviceWithPhantomRetry(deviceId, publicKeys, stored ?? null, get, set),
            onBlocked: () => {
              // Surface the existing "blocked — clear data and re-login" UX. Fired
              // for a local cascade-debounce block or a server reactivation refusal.
              window.alert(
                "Dieses Gerät hatte einen Verschlüsselungsfehler. Bitte abmelden, Browser-Daten für app.kollegeplus.de löschen und neu anmelden.",
              );
            },
            emit: (ev) =>
              reportE2eeError({
                phase: "e2ee_store_wipe_detected",
                error: `priorDeviceId=${ev.priorDeviceId} outcome=${ev.outcome}${ev.newDeviceId ? ` newDeviceId=${ev.newDeviceId}` : ""}${ev.error ? ` error=${ev.error}` : ""}`,
              }),
            log: { info: (...a) => e2eeLog.info(...a), warn: (...a) => e2eeLog.warn(...a) },
          },
        );
        if (!newDeviceId) {
          // Blocked or registration failed — maybeRekey already surfaced the UX /
          // telemetry. Do not flip initialized; bail so the auth layer / next boot
          // can retry, and so a cascade-block isn't raced.
          set({ isInitializing: false });
          return;
        }
        deviceId = newDeviceId;
        localStorage.setItem("kp_keys_fingerprint", keysFingerprint);
        // Redistribution to the fresh device is handled by F23b on WS-connect
        // (useChat.ts:2108) — a wiped device is keyless in every group channel,
        // so F23b fires request_channel_redistribute for all of them with dedupe
        // + telemetry. No extra trigger here.
        e2eeLog.info("Device re-keyed onto fresh id after key-store wipe", deviceId);
      } else if (needsRegistration) {
        // Must be fatal: peers fetch this device's bundle via /users/keys to
        // build X3DH sessions. If we set initialized=true while the server has
        // no keys for us, every peer's send fails and we can't decrypt their
        // initial messages. Let the exception propagate to the outer catch so
        // initialized stays false and the auth layer can surface the error.
        // F15b: on 409 device_deactivated_use_fresh_id, mint a new deviceId.
        // F22c Task 3: if idb_device_mismatch is thrown, another tab has already
        // registered under a different deviceId — reload IDB state and use it
        // rather than failing init entirely.
        let idbMismatchAdopted = false;
        try {
          deviceId = await _registerDeviceWithPhantomRetry(
            deviceId,
            publicKeys,
            stored ?? null,
            get,
            set,
          );
        } catch (regErr) {
          if (regErr instanceof Error && regErr.message.startsWith("idb_device_mismatch")) {
            // Another tab rotated the device; reload IDB and adopt its state.
            e2eeLog.warn("[F22c] init needsRegistration: idb_device_mismatch — reloading IDB state", regErr.message);
            const idbReloaded = await getFirstDeviceKeys();
            if (!idbReloaded) throw regErr; // IDB empty: nothing to recover from, surface original error
            stored = idbReloaded;
            deviceId = idbReloaded.deviceId;
            deviceKeys = deserializeDeviceKeys(idbReloaded);
            localStorage.setItem("kollegeplus:deviceId", deviceId);
            // The other tab already registered; adopt fingerprint so we don't re-register again.
            const reloadedPk = exportPublicKeys(deviceKeys);
            localStorage.setItem("kp_keys_fingerprint", reloadedPk.identityKey + ":" + reloadedPk.signingKey);
            // Also adopt the IDK fingerprint of the reloaded material so the next
            // /auth/refresh validates against the bundle the other tab published —
            // otherwise the stale init-time fp (computed at :626 for the OLD keys)
            // would mismatch and trigger a full crypto wipe.
            try {
              const reloadedIdkFp = await computeIdkFingerprint(
                deviceKeys.identityKeyPair.curve25519.publicKey,
                deviceKeys.identityKeyPair.ed25519.publicKey,
                deviceKeys.signedPreKey.keyPair.publicKey,
              );
              localStorage.setItem("kp_idk_fingerprint", reloadedIdkFp);
            } catch (fpErr) {
              e2eeLog.warn("[F22c] init adopt: idk fingerprint recompute failed (non-fatal)", fpErr);
            }
            e2eeLog.info("[F22c] init: adopted IDB state from other tab", deviceId);
            // Fall through — init continues with the reloaded state; no server call needed.
            idbMismatchAdopted = true;
          } else {
            throw regErr;
          }
        }
        // Store fingerprint only on the normal (server-POST) path; the mismatch-
        // reload path already wrote the correct fingerprint for the adopted IDB state.
        if (!idbMismatchAdopted) {
          localStorage.setItem("kp_keys_fingerprint", keysFingerprint);
        }
        e2eeLog.info("Device keys registered with server");
      } else {
        e2eeLog.debug("Device keys unchanged — skipping registration");
        // Just update last_active timestamp (lightweight, no key data)
        try {
          // F22b D3: also send local SPK keyId + IDK fingerprint for drift detection.
          const hbRes = await api.post<{ success: boolean; reregister?: boolean; serverPreKeyCount?: number; spkDrift?: boolean }>("/users/me/devices/heartbeat", {
            deviceId,
            spkKeyId: deviceKeys.signedPreKey.keyId,
            idkFingerprintLocal: localStorage.getItem("kp_idk_fingerprint") ?? undefined,
          });
          if (hbRes?.reregister && hbRes?.spkDrift) {
            // F22c FC1 (follow-up): init-path drift wiring — mirrors idle-heartbeat
            // block at ~:360-379. Same deps: reloadFromIdb → getFirstDeviceKeys,
            // register → _registerDeviceWithPhantomRetry + zustand sync + idk fp rewrite.
            e2eeLog.warn("[F22c] Init heartbeat: server reported SPK drift — triggering IDB-truth re-register");
            void triggerDriftSelfHeal();
          } else if (hbRes?.spkDrift) {
            // spkDrift without reregister: server detected drift but didn't ask
            // for re-registration yet — log for visibility, heal on next heartbeat.
            e2eeLog.warn("[F22c] Init heartbeat: server reported SPK/IDK drift (no reregister flag)");
          }
          if (typeof hbRes?.serverPreKeyCount === "number") {
            _serverPreKeyCount = hbRes.serverPreKeyCount;
            e2eeLog.log(`Server reports ${_serverPreKeyCount} pre-keys for this device`);
          }
          // If server says re-register (device gone or no pre-keys), do it immediately.
          // spkDrift case is already handled above via handleDriftReregister (void/async).
          if (hbRes?.reregister && !hbRes?.spkDrift) {
            e2eeLog.warn("Server requested re-registration (device gone or pre-keys depleted)");
            deviceId = await _registerDeviceWithPhantomRetry(
              deviceId,
              publicKeys,
              stored ?? null,
              get,
              set,
            );
            localStorage.setItem("kp_keys_fingerprint", keysFingerprint);
            e2eeLog.info("Device keys re-registered successfully");
          }
        } catch (hbErr) {
          // Heartbeat failed — force re-registration as fallback
          e2eeLog.warn("Heartbeat failed — re-registering keys", hbErr);
          try {
            deviceId = await _registerDeviceWithPhantomRetry(
              deviceId,
              publicKeys,
              stored ?? null,
              get,
              set,
            );
            localStorage.setItem("kp_keys_fingerprint", keysFingerprint);
            e2eeLog.info("Device keys re-registered after heartbeat failure");
          } catch (regErr) {
            e2eeLog.warn("Re-registration also failed:", regErr);
          }
        }
      }

      // Sessions will be lazily loaded when needed
      const sessions = new Map<string, CryptoSession>();

      // One-time migration: clear stale bot sessions from IDB
      // (Bot sessions were reset server-side, client must also clear)
      const botSessionMigrationKey = "kp_bot_session_reset_v2";
      if (!localStorage.getItem(botSessionMigrationKey)) {
        try {
          const botUserId = "028406fb-314a-407e-85bf-12a1f7c22500";
          const cleared = await deleteSessionsForPeer(botUserId);
          if (cleared > 0) {
            e2eeLog.warn(`Cleared ${cleared} stale bot sessions (one-time migration)`);
          }
          localStorage.setItem(botSessionMigrationKey, new Date().toISOString());
        } catch {
          // Non-critical
        }
      }

      // Try to restore backup key from localStorage cache (for session restore)
      let cachedBackupKey: CryptoKey | null = null;
      try {
        cachedBackupKey = await restoreBackupKeyFromCache();
      } catch {
        // Non-critical
      }

      // Replenish pre-keys BEFORE flipping initialized=true. Otherwise an
      // incoming X3DH message that references an OPK the server already
      // consumed (drift between our local list and server reality) decrypts
      // before our refill upload lands — peer's first message is then lost.
      // Failure is still logged-only: a transient network blip shouldn't block
      // login, and the next encryptForAllDevices/heartbeat will re-attempt.
      try {
        await _checkAndReplenishPreKeys(get, set, _serverPreKeyCount);
      } catch (err) {
        e2eeLog.warn("Pre-key replenishment on init failed (non-fatal):", err);
      }

      set({
        initialized: true,
        deviceId,
        deviceKeys,
        sessions,
        isInitializing: false,
        backupKey: cachedBackupKey,
      });

      // Plaintext-cache telemetry: bind deviceId + start periodic flush.
      void import("../lib/cache-telemetry").then(({ setCacheTelemetryDeviceId, startCacheTelemetry }) => {
        setCacheTelemetryDeviceId(deviceId);
        startCacheTelemetry();
      }).catch(() => {});

      // F19: backup-mechanism telemetry — per-mechanism write/read/decrypt counters.
      void import("../lib/backup-telemetry").then(({ startBackupTelemetry }) => {
        startBackupTelemetry();
      }).catch(() => {});

      e2eeLog.log("Initialized with device", deviceId, cachedBackupKey ? "(backup key loaded)" : "(no backup key)");

      // Task 6: post-wipe sender-key redistribution to the fresh device is owned
      // by F23b (useChat.ts:2108, request_channel_redistribute on WS-connect),
      // which fires for every keyless group channel — exactly the state a wipe
      // produces — with its own dedupe + redistribute_request_sent telemetry, and
      // re-fires on every reconnect. The durable lastKnownDeviceId marker for the
      // fresh id was already refreshed inside maybeRekey. Nothing to do here.

      // Bug D: if no user_backup_key is cached, request it from peer devices.
      // Fire-and-forget — never blocks init. The in-memory flag prevents spam.
      if (!cachedBackupKey) {
        // Defer slightly so the WS connection is established before we send.
        setTimeout(() => requestUserBackupKey(), 2_000);
      }

      // Option E — async restore of sender-key sessions from server backup.
      // Non-blocking: init returns immediately. Decrypts that fire before this
      // resolves use whatever is in IDB; subsequent decrypts benefit from the
      // restored keys. Only runs when we already have a cached backup key —
      // password-based initBackupKey runs its own restore flow downstream.
      if (cachedBackupKey) {
        void import("../lib/session-backup")
          .then(({ restoreSessionBackups }) => restoreSessionBackups())
          .then((n) => { if (n > 0) e2eeLog.log("Restored", n, "sender key sessions from server backup"); })
          .catch((err) => e2eeLog.warn("Sender key session restore failed:", err));
      }

      _ensureHeartbeatScheduler(get, set);

      // F22c Task 4: cross-tab device-key change notification.
      // Subscribe once (module-level guard) so that when another tab writes
      // new device-keys (drift-heal, phantom-retry, replenish) and broadcasts
      // the change, this tab reloads its crypto state from IDB instead of
      // continuing to publish/wrap with stale in-memory keys.
      //
      // Loop-safety: the subscriber acts when the deviceId DIFFERS, or when the
      // SPK keyId differs under the SAME deviceId (a sibling tab rotated/healed
      // its signed-pre-key). After re-init this tab reads IDB and adopts whatever
      // (deviceId, spkKeyId) is there; subsequent broadcasts for that same pair
      // compare equal → no second re-init. Guarding on deviceId alone left two
      // tabs sharing one deviceId fighting over divergent SPK material, which the
      // server flagged as drift every heartbeat → endless reinit (the bug).
      if (!tabSyncSubscribed) {
        tabSyncSubscribed = true;
        onDeviceKeysChanged(({ deviceId: changedId, spkKeyId }) => {
          const st = get();
          const current = st.deviceId;
          if (!current) return;
          const idChanged = changedId !== current;
          const currentSpkKeyId = st.deviceKeys?.signedPreKey.keyId;
          const spkChanged =
            !idChanged &&
            spkKeyId !== undefined &&
            currentSpkKeyId !== undefined &&
            spkKeyId !== currentSpkKeyId;
          if (idChanged || spkChanged) {
            e2eeLog.warn(
              "[F22c] Device keys changed in another tab (changedId=%s currentId=%s spkKeyId=%s currentSpkKeyId=%s) — reloading crypto state",
              changedId,
              current,
              String(spkKeyId),
              String(currentSpkKeyId),
            );
            set({ initialized: false, isInitializing: false });
            void get().initialize();
          }
        });
      }

    } catch (err) {
      e2eeLog.error("Initialization failed:", err);
      set({ isInitializing: false });
    }
  },

  // ── Session management — delegated to useCrypto-sessions.ts ──

  getOrCreateSession: (peerId) => _getOrCreateSession(peerId, get, set),

  getOrCreateSessionForDevice: (peerId, peerDeviceId, peerDevice) =>
    _getOrCreateSessionForDevice(peerId, peerDeviceId, peerDevice, get, set),

  // ── Encrypt/Decrypt — delegated to useCrypto-encrypt.ts ──
  // Wrapped in withBusy() so the auto-update/unload machinery knows a
  // multi-step E2EE operation is in flight and must not be interrupted.

  encryptForAllDevices: (recipientIds, plaintext, meta) =>
    withBusy(() => { guardCryptoHealthy(get); return _encryptForAllDevices(recipientIds, plaintext, get, meta); }),

  encryptForPeer: (peerId, plaintext) =>
    withBusy(() => { guardCryptoHealthy(get); return _encryptForPeer(peerId, plaintext, get); }),

  decryptMessage: (senderId, envelopeStr) =>
    withBusy(() => _decryptMessage(senderId, envelopeStr, get, set)),

  // ── Group encryption — delegated to useCrypto-group.ts ──

  encryptForGroup: (channelId, memberIds, plaintext) =>
    withBusy(() => { guardCryptoHealthy(get); return _encryptForGroup(channelId, memberIds, plaintext, get); }),

  decryptGroupMessage: (senderId, channelId, envelopeStr, messageId) =>
    withBusy(() => _decryptGroupMessage(senderId, channelId, envelopeStr, messageId)),

  decryptBotChannelSenderKeyMessage: (channelId, botUserId, env, messageId) =>
    withBusy(() => _decryptBotChannelSenderKeyMessage(channelId, botUserId, env, messageId)),

  rotateSenderKey: (channelId, memberIds) =>
    withBusy(() => _rotateSenderKey(channelId, memberIds, get)),

  // ── PreKey management — delegated to useCrypto-prekeys.ts ──

  checkAndReplenishPreKeys: () => _checkAndReplenishPreKeys(get, set),

  eraseAll: async () => {
    // Option E — also wipe server-side sender-key backups so a fresh login
    // starts from a clean slate. Best-effort; do not block local erase on
    // network failure.
    try {
      const { deleteServerSessionBackups } = await import("../lib/session-backup");
      await deleteServerSessionBackups();
    } catch (err) {
      e2eeLog.warn("[session-backup] eraseAll cleanup failed:", err);
    }
    await eraseAllCryptoData();
    localStorage.removeItem("kollegeplus:deviceId");
    clearBackupKeyCache();
    set({
      initialized: false,
      deviceId: null,
      deviceKeys: null,
      sessions: new Map(),
      backupKey: null,
    });
    e2eeLog.log("All crypto data erased");
  },

  // ── Ansatz C: Password-based Backup Key ──

  initBackupKey: async (password: string) => {
    try {
      // Check if this is a new device (no cached backup key)
      const hadCachedKey = !!localStorage.getItem("kp_backup_key");

      const key = await _initBackupKey(password);
      if (key) {
        set({ backupKey: key });
        e2eeLog.log("[backup-key] Initialized successfully");

        // Option E — restore sender-key sessions from server backup now that
        // the backup key is available. Non-blocking so the rest of init can
        // proceed; subsequent group decrypts pick up the freshly restored
        // sender keys via IDB.
        void import("../lib/session-backup")
          .then(({ restoreSessionBackups }) => restoreSessionBackups())
          .then((n) => { if (n > 0) e2eeLog.log("[backup-key] Restored", n, "sender key sessions from server backup"); })
          .catch((err) => e2eeLog.warn("[backup-key] Sender key session restore failed:", err));

        // Trigger message reload so any "[Verschlüsselte Nachricht]" placeholders
        // that appeared because backupKey wasn't ready yet get re-decrypted now.
        // This covers the race: initialized=true fires before initBackupKey resolves,
        // so tryBackupDecrypt returned BACKUP_KEY_NOT_READY for those messages.
        try {
          const { useChat } = await import("./useChat");
          useChat.getState().clearMessagesCache();
          e2eeLog.log("[backup-key] Triggered message cache clear after backup key ready");
        } catch (reloadErr) {
          e2eeLog.warn("[backup-key] Could not reload messages after backup key init:", reloadErr);
        }

        // If this is a new device, restore message history from backups
        if (!hadCachedKey) {
          e2eeLog.log("[backup-key] New device detected — restoring backups...");
          try {
            const { api: apiInner } = await import("../lib/api");
            const channelData = await apiInner.get<{ channels: Array<{ id: string }> }>("/channels");
            const channels = channelData?.channels ?? [];

            let totalRestored = 0;
            for (const channel of channels) {
              const restored = await restoreBackups(key, channel.id);
              for (const entry of restored) {
                // Write to IDB cache (persistent)
                await cachePlaintext(entry.messageId, entry.plaintext);
                // Also write to in-memory cache (immediate availability, Safari IDB timing workaround)
                _restoredPlaintexts.set(entry.messageId, entry.plaintext);
              }
              totalRestored += restored.length;
            }
            e2eeLog.log(`[backup-key] Restored ${totalRestored} messages from backup`);
            // Reload again after backup restore so newly cached messages appear
            try {
              const { useChat } = await import("./useChat");
              useChat.getState().clearMessagesCache();
            } catch { /* non-critical */ }
          } catch (restoreErr) {
            console.error("[backup-key] Restore failed:", restoreErr);
          }
        }
      }
    } catch (err) {
      console.error("[backup-key] Init failed:", err);
    }
  },

  uploadMessageBackup: (messageId: string, plaintext: string): Promise<boolean> => {
    const key = get().backupKey;
    if (!key) return Promise.resolve(false);
    return _uploadBackup(key, messageId, plaintext);
  },

  deleteSessionForPeer: (peerId: string) =>
    withBusy(async () => {
      // Delete from IndexedDB
      const deleted = await deleteSessionsForPeer(peerId);
      // Delete from in-memory sessions
      set((s) => {
        const sessions = new Map(s.sessions);
        for (const key of sessions.keys()) {
          if (typeof key !== "string") continue;
          if (key.startsWith(`${peerId}:`)) {
            sessions.delete(key);
          }
        }
        return { sessions };
      });
      e2eeLog.log(` Deleted ${deleted} sessions for peer ${peerId}`);
    }),
}));

// ─── F22c Task 5: shared drift self-heal helper ─────────────────────────────
// Exported so the wrap-time drift guard in useCrypto-group.ts can trigger the
// same IDB-truth re-register without duplicating the full deps construction
// (reloadFromIdb + register with zustand sync + idk fingerprint rewrite).
// Identical semantics to the idle/init heartbeat drift branches above.
export async function triggerDriftSelfHeal(): Promise<void> {
  const { handleDriftReregister } = await import("../lib/drift-reregister");
  const store = useCrypto.getState();
  if (!store.deviceId || !store.deviceKeys) return;
  void handleDriftReregister({
    reloadFromIdb: async () => (await getFirstDeviceKeys()) ?? null,
    register: async (stored) => {
      const dk = deserializeDeviceKeys(stored);
      const pk = exportPublicKeys(dk);
      const id = await _registerDeviceWithPhantomRetry(
        stored.deviceId,
        pk,
        stored,
        () => useCrypto.getState(),
        (partial) => useCrypto.setState(partial),
      );
      localStorage.setItem("kp_keys_fingerprint", pk.identityKey + ":" + pk.signingKey);
      useCrypto.setState({ deviceId: id, deviceKeys: dk });
      try {
        const idkFp = await computeIdkFingerprint(
          dk.identityKeyPair.curve25519.publicKey,
          dk.identityKeyPair.ed25519.publicKey,
          dk.signedPreKey.keyPair.publicKey,
        );
        localStorage.setItem("kp_idk_fingerprint", idkFp);
      } catch (fpErr) {
        e2eeLog.warn("[F22c] wrap-guard drift heal: idk fingerprint recompute failed (non-fatal)", fpErr);
      }
      return id;
    },
  }).then((healed) => {
    if (healed) e2eeLog.info("F22c: wrap-guard drift re-register completed — IDB truth published");
  }).catch((err) => e2eeLog.warn("F22c: wrap-guard drift re-register failed", err));
}
